import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { BoatCardImage } from '@components/BoatCardImage';
import { useAddToFavoriteBoat, useRemoveFavoriteBoat, useUserFavorites } from '@hooks';
import { PublicBoatCard } from '@marine/boats/src/components/PublicBoatCard';
import { getBoatName } from '@utils/getBoatName';
import { useSize } from 'ahooks';
import { Button, Grid, Space } from 'antd';
import cn from 'classnames';
import { useKeenSlider } from 'keen-slider/react';
import Link from 'next/link';
import { FC, useMemo, useRef, useState } from 'react';
import { CarouselControllersTop } from 'sailor-ui/CarouselControllersTop';
import styles from './ExplorerBoatsCarousel.module.scss';
import { ExplorerBoatsCarouselProps } from './ExplorerBoatsCarousel.props';

const { useBreakpoint } = Grid;
const ExplorerBoatsCarousel: FC<ExplorerBoatsCarouselProps> = ({ subtitle, more, title, badge, boats }) => {
  const refSliderWrapper = useRef(null);
  const size = useSize(refSliderWrapper);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const { isAuthenticated } = useAuth0();

  const { md } = useBreakpoint();

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: 'snap',
    slides: {
      perView: 'auto',
      spacing: 0,
    },
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  const { reFetchFavorite, checkIsFavorite } = useUserFavorites();
  const { isLoadingRemove, removeFavoriteFn } = useRemoveFavoriteBoat();
  const { isLoadingAdd, addToFavoriteFn } = useAddToFavoriteBoat();

  const nrOfDots = useMemo(() => {
    if (boats?.length) {
      if (size?.width) {
        const itemsPerPage = Math.ceil(size?.width / 270);

        if (boats?.length > itemsPerPage) {
          return boats?.length - itemsPerPage + 2;
        }
      }
    }

    return 0;
  }, [boats?.length, size]);

  return (
    <section className={styles.container} ref={refSliderWrapper}>
      <header className={styles.carouselHeader}>
        <div>
          <div className={styles.contentTitle}>
            <h3 className={`${styles.moreText}`}>{subtitle}</h3>
            {badge && <div className={styles.badge}>{badge}</div>}
          </div>

          <Space align="baseline" size={15} direction={md ? 'horizontal' : 'vertical'}>
            <h2>{title}</h2>
            <Link href={more.url} className={styles.moreLink}>
              {more.text} <RightOutlined />
            </Link>
          </Space>
        </div>
        {loaded && instanceRef.current && !!nrOfDots && (
          <CarouselControllersTop currentSlide={currentSlide} instanceRef={instanceRef} nrOfDots={nrOfDots} />
        )}
      </header>

      <div ref={ref} className="keen-slider">
        {boats.map((item) => {
          const isFavourited = checkIsFavorite(Number(item.documentId));

          return (
            <Link
              className={cn('keen-slider__slide', styles.boatCardWrapper)}
              href={`/boat/${item.slug}`}
              key={item.documentId}
            >
              <PublicBoatCard
                showFavoriteToggle={isAuthenticated}
                key={item.documentId}
                className={styles.publicBoatCardStyle}
                title={getBoatName(item)}
                boatImage={<BoatCardImage image={item.image} height={200} width={300} />}
                isFavorite={isFavourited}
                disableFavoriteButton={isLoadingAdd || isLoadingRemove}
                boatModel={item.model}
                // favorites_count={item.favorites_count}
                views={item.views_count}
                onFavoriteClick={() => {
                  if (!isFavourited) {
                    addToFavoriteFn(Number(item.documentId), {
                      onSettled: () => {
                        reFetchFavorite();
                      },
                    });
                  } else {
                    removeFavoriteFn(Number(item.documentId), {
                      onSettled: () => {
                        reFetchFavorite();
                      },
                    });
                  }
                }}
              />
            </Link>
          );
        })}
        {/* <div className="keen-slider__slide number-slide1">1</div>
        <div className="keen-slider__slide number-slide2">2</div>
        <div className="keen-slider__slide number-slide3">3</div>
        <div className="keen-slider__slide number-slide4">4</div>
        <div className="keen-slider__slide number-slide5">5</div>
        <div className="keen-slider__slide number-slide6">6</div> */}
      </div>
    </section>
  );
};

export { ExplorerBoatsCarousel };
